import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { listFiles, validateFile, getSettings, listSchemaVersions } from "@/lib/api";
import ValidationResult from "@/components/ValidationResult";

export default function ValidatePage() {
  const [files, setFiles] = useState([]);
  const [fileId, setFileId] = useState("");
  const [busy, setBusy] = useState(false);
  const [validation, setValidation] = useState(null);
  const [versions, setVersions] = useState([]);
  const [version, setVersion] = useState("");

  useEffect(() => {
    listFiles().then(setFiles);
    listSchemaVersions().then(setVersions);
    getSettings().then((s) => setVersion(s.schema_version || ""));
  }, []);

  const run = async () => {
    if (!fileId) {
      toast.error("Select a file to validate");
      return;
    }
    setBusy(true);
    setValidation(null);
    try {
      const res = await validateFile(fileId, version || undefined);
      setValidation(res);
      toast.success(`${res.summary.verdict} · ${res.summary.compliance_pct}% compliant`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Validation failed");
    } finally {
      setBusy(false);
    }
  };

  const activeV = versions.find((v) => v.id === version);

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center justify-between px-8 sticky top-0 bg-[#0A0A0A]/85 backdrop-blur z-10">
        <div>
          <h1 className="font-display text-xl font-bold tracking-tight">Validate</h1>
          <p className="font-mono text-[11px] text-zinc-500">
            ADAC schema conformance — mandatory attributes &amp; structure {activeV ? `(${activeV.label})` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={version} onValueChange={setVersion}>
            <SelectTrigger data-testid="validate-version-select" className="w-40 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-xs">
              <SelectValue placeholder="Schema version" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm">
              {versions.map((v) => (
                <SelectItem key={v.id} value={v.id} className="font-mono text-xs">{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={fileId} onValueChange={setFileId}>
            <SelectTrigger data-testid="validate-file-select" className="w-64 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-sm">
              <SelectValue placeholder="Select a document…" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm">
              {files.map((f) => (
                <SelectItem key={f.id} value={f.id} className="font-mono text-xs">{f.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button data-testid="run-validate-btn" onClick={run} disabled={busy} className="rounded-sm bg-blue-600 hover:bg-blue-500 text-white">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BadgeCheck className="w-4 h-4 mr-2" />}
            Validate
          </Button>
        </div>
      </header>

      <div className="p-8">
        {validation ? (
          <ValidationResult validation={validation} />
        ) : (
          <div className="grid-texture border border-zinc-800 rounded-sm py-24 flex flex-col items-center text-center">
            <BadgeCheck className="w-10 h-10 text-zinc-700 mb-4" />
            <p className="text-sm text-zinc-500 max-w-md">
              Validate an ADAC XML against the official schema — checks mandatory global
              attributes (Status), per-feature mandatory fields, ObjectId uniqueness and
              geometry presence, flagging errors &amp; warnings like a Receiving Entity conformance check.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
